import React, { useEffect, useRef, useState } from 'react';

/**
 * Synchronously detect the reduced-motion preference so above-the-fold
 * reveals never start hidden for users who asked for no motion (WCAG 2.3.3 /
 * respect prefers-reduced-motion). Safe on SSR (returns false).
 */
const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

// Exponential ease-out (≈ easeOutQuint). Decelerates far more gracefully than
// CSS `ease-out`, which is what gives reveals the premium/"smooth" settle used
// across the polished templates (Vital/Glam). Single source so every section
// reveal shares the same motion signature.
const REVEAL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Offset is deliberately small — a big travel reads as "jumpy"; a short rise
// under a long exponential ease reads as "settled".
const REVEAL_OFFSET = 24;

export interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  threshold?: number;
  className?: string;
}

/**
 * FadeIn Animation Component
 * 
 * Animates children with fade-in effect when they enter viewport
 * Uses Intersection Observer for performance
 */
export const FadeIn: React.FC<FadeInProps> = ({
  children,
  delay = 0,
  duration = 600,
  direction = 'up',
  threshold = 0.1,
  className = '',
}) => {
  // Users with prefers-reduced-motion see content immediately — no reveal.
  const reduceMotion = prefersReducedMotion();
  const [isVisible, setIsVisible] = useState(reduceMotion);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduceMotion) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Unobserve after animation triggers (performance)
          observer.unobserve(entry.target);
        }
      },
      // Small negative bottom margin so a section reveals just after it starts
      // entering the viewport (deliberate) rather than the instant its top edge
      // clips in — reads as intentional, not a pop at the fold.
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, reduceMotion]);

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up':
          return `translateY(${REVEAL_OFFSET}px)`;
        case 'down':
          return `translateY(-${REVEAL_OFFSET}px)`;
        case 'left':
          return `translateX(${REVEAL_OFFSET}px)`;
        case 'right':
          return `translateX(-${REVEAL_OFFSET}px)`;
        default:
          return 'none';
      }
    }
    return 'none';
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        // Hint the compositor while hidden; release once settled so we don't
        // pin a layer forever (which itself can cause jank on long pages).
        willChange: reduceMotion || isVisible ? 'auto' : 'opacity, transform',
        transition: reduceMotion
          ? 'none'
          : `opacity ${duration}ms ${REVEAL_EASE} ${delay}ms, transform ${duration}ms ${REVEAL_EASE} ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export const AnimationWrapper: React.FC<{
  children: React.ReactNode;
  enabled?: boolean;
} & FadeInProps> = ({ children, enabled, ...props }) => {
  if (!enabled) return <>{children}</>;
  return (
    <FadeIn {...props}>
      {children}
    </FadeIn>
  );
};

export default FadeIn;
